import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAdminMerchantSubs, updateAdminMerchantSub, getAdminSubPlans } from "../../../redux/actions/profile";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

const LEVEL_LABEL = { 0: "Free", 1: "Starter", 2: "Business", 3: "Pro" };
const LEVEL_COLOR = { 0: "#aaa", 1: "#4fb0c6", 2: "#f4a261", 3: "#a7cc48" };

export default function MerchantSubscriptions() {
  const dispatch = useDispatch();
  const merchants = useSelector((s) => s.profile.adminMerchantSubs);
  const meta = useSelector((s) => s.profile.adminMerchantSubsMeta);
  const plans = useSelector((s) => s.profile.adminSubPlans);

  const [status, setStatus] = useState("");
  const [plan, setPlan] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = (p = page) => {
    const params = { page: p, limit: 50 };
    if (status) params.status = status;
    if (plan) params.plan = plan;
    dispatch(getAdminMerchantSubs(params));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(1); dispatch(getAdminSubPlans()); }, [dispatch]);

  const openEdit = (m) => {
    setEditing(m);
    setForm({
      subscriptionLevel: m.subscriptionLevel ?? 0,
      plan: m.plan || "",
      cycle: m.cycle || 1,
      price: m.price || 0,
      subscriptionStart: m.subscriptionStart ? new Date(m.subscriptionStart).toISOString().split("T")[0] : "",
      subscriptionEnd: m.subscriptionEnd ? new Date(m.subscriptionEnd).toISOString().split("T")[0] : ""
    });
    setMsg(null);
  };

  const submit = async () => {
    if (!editing) return;
    setSubmitting(true); setMsg(null);
    const body = {
      subscriptionLevel: Number(form.subscriptionLevel),
      plan: form.plan,
      cycle: Number(form.cycle),
      price: Number(form.price),
      subscriptionStart: form.subscriptionStart ? new Date(form.subscriptionStart).getTime() : undefined,
      subscriptionEnd: form.subscriptionEnd ? new Date(form.subscriptionEnd).getTime() : undefined
    };
    const res = await dispatch(updateAdminMerchantSub(editing._id, body));
    setSubmitting(false);
    if (res?.success) {
      setMsg({ type: "success", text: "Subscription updated" });
      load();
      setTimeout(() => setEditing(null), 700);
    } else {
      setMsg({ type: "error", text: res?.message || "Update failed" });
    }
  };

  const fmtDate = (ts) => ts ? new Date(ts).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div style={S.root}>
      <div style={S.pageHeader}>
        <div>
          <h2 style={S.heading}>Merchant Subscriptions</h2>
          <p style={S.headingSub}>{meta?.total || merchants.length} merchants</p>
        </div>
        <span style={S.countBadge}>{meta?.total || merchants.length}</span>
      </div>

      <div style={S.filterBar}>
        <div style={S.filterGroup}>
          <label style={S.filterLabel}>Status</label>
          <select style={S.select} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All Merchants</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="free">Free</option>
          </select>
        </div>
        <div style={S.filterGroup}>
          <label style={S.filterLabel}>Plan</label>
          <select style={S.select} value={plan} onChange={(e) => setPlan(e.target.value)}>
            <option value="">All Plans</option>
            {plans.map((p) => <option key={p._id} value={p.name}>{p.name}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", gap: 8, alignSelf: "flex-end" }}>
          <button style={S.btn} onClick={() => { setPage(1); load(1); }}>Apply Filter</button>
          <button style={S.btnSecondary} onClick={() => { setStatus(""); setPlan(""); setPage(1); dispatch(getAdminMerchantSubs({ page: 1, limit: 50 })); }}>Reset</button>
        </div>
      </div>

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              {["Merchant", "Plan", "Level", "Status", "Expires", "Cycle", "Actions"].map((h) => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {merchants.length === 0 ? (
              <tr><td colSpan={7} style={{ ...S.td, textAlign: "center", color: "#bbb", padding: 32 }}>No merchants found</td></tr>
            ) : merchants.map((m) => (
              <tr key={m._id} style={S.tr}>
                <td style={S.td}>
                  <div style={{ fontWeight: 600, color: BG, fontSize: 13 }}>{m.storeInfo?.storeName || m.fullname || "—"}</div>
                  <div style={{ fontSize: 11, color: "#999" }}>{m.email}</div>
                </td>
                <td style={S.td}>{m.plan || "—"}</td>
                <td style={S.td}>
                  <span style={{ background: (LEVEL_COLOR[m.subscriptionLevel] || "#888") + "18", color: LEVEL_COLOR[m.subscriptionLevel] || "#888", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                    {LEVEL_LABEL[m.subscriptionLevel] ?? m.subscriptionLevel}
                  </span>
                </td>
                <td style={S.td}>
                  <span style={{ background: m.isActive ? "rgba(46,196,182,0.12)" : m.isExpired ? "rgba(230,57,70,0.1)" : "#f5f5f5", color: m.isActive ? "#2ec4b6" : m.isExpired ? "#e63946" : "#aaa", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                    {m.isActive ? "Active" : m.isExpired ? "Expired" : "Free"}
                  </span>
                </td>
                <td style={{ ...S.td, color: "#888" }}>{fmtDate(m.subscriptionEnd)}</td>
                <td style={S.td}>{m.cycle ? `${m.cycle} mo` : "—"}</td>
                <td style={S.td}>
                  <button style={S.editBtn} onClick={() => openEdit(m)}>
                    <i className="bi bi-pencil" style={{ marginRight: 4 }} />Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={S.pagination}>
        <button style={S.pgBtn} disabled={page <= 1} onClick={() => { const p = page - 1; setPage(p); load(p); }}>‹ Previous</button>
        <span style={{ color: "#888", fontSize: 13 }}>Page {page}</span>
        <button style={S.pgBtn} onClick={() => { const p = page + 1; setPage(p); load(p); }}>Next ›</button>
      </div>

      {editing && (
        <div style={S.overlay} onClick={() => setEditing(null)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <div style={S.modalTitle}>Update Subscription</div>
                <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{editing.storeInfo?.storeName || editing.email}</div>
              </div>
              <button style={S.closeBtn} onClick={() => setEditing(null)}>✕</button>
            </div>
            <div style={S.modalBody}>
              <div style={S.fieldRow}>
                <div style={S.field}>
                  <label style={S.label}>Subscription Level</label>
                  <select style={S.input} value={form.subscriptionLevel} onChange={(e) => setForm({ ...form, subscriptionLevel: e.target.value })}>
                    <option value={0}>0 — Free</option>
                    <option value={1}>1 — Starter</option>
                    <option value={2}>2 — Business</option>
                    <option value={3}>3 — Pro</option>
                  </select>
                </div>
                <div style={S.field}>
                  <label style={S.label}>Plan Name</label>
                  <select style={S.input} value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
                    <option value="">— Select —</option>
                    {plans.map((p) => <option key={p._id} value={p.name}>{p.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={S.fieldRow}>
                <div style={S.field}>
                  <label style={S.label}>Start Date</label>
                  <input style={S.input} type="date" value={form.subscriptionStart} onChange={(e) => setForm({ ...form, subscriptionStart: e.target.value })} />
                </div>
                <div style={S.field}>
                  <label style={S.label}>End Date</label>
                  <input style={S.input} type="date" value={form.subscriptionEnd} onChange={(e) => setForm({ ...form, subscriptionEnd: e.target.value })} />
                </div>
              </div>
              <div style={S.fieldRow}>
                <div style={S.field}>
                  <label style={S.label}>Cycle (months)</label>
                  <input style={S.input} type="number" min="1" value={form.cycle} onChange={(e) => setForm({ ...form, cycle: e.target.value })} />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Price (₦)</label>
                  <input style={S.input} type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                </div>
              </div>
              {msg && (
                <div style={{ borderRadius: 8, padding: "10px 14px", fontSize: 13, marginTop: 12, background: msg.type === "success" ? "rgba(46,196,182,0.1)" : "rgba(230,57,70,0.08)", color: msg.type === "success" ? "#2ec4b6" : "#e63946", border: `1px solid ${msg.type === "success" ? "#2ec4b6" : "#e63946"}` }}>
                  {msg.text}
                </div>
              )}
              <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                <button style={S.btn} onClick={submit} disabled={submitting}>{submitting ? "Saving…" : "Update"}</button>
                <button style={S.btnSecondary} onClick={() => setEditing(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  root: { paddingBottom: 40 },
  pageHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 24 },
  heading: { fontSize: 22, fontWeight: 800, color: BG, margin: 0 },
  headingSub: { fontSize: 13, color: "#888", marginTop: 4 },
  countBadge: { background: "rgba(167,204,72,0.12)", color: "#5a8a00", border: "1px solid " + PRIMARY, borderRadius: 20, padding: "5px 14px", fontSize: 12, fontWeight: 700 },
  filterBar: { background: "#fff", borderRadius: 10, padding: "16px 20px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 20, alignItems: "flex-end" },
  filterGroup: { display: "flex", flexDirection: "column", gap: 4 },
  filterLabel: { fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 },
  select: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "#333", outline: "none", background: "#fafafa" },
  btn: { background: BG, border: "none", borderRadius: 8, color: "#fff", fontWeight: 600, padding: "8px 20px", cursor: "pointer", fontSize: 13, display: "inline-flex", alignItems: "center" },
  btnSecondary: { background: "#f5f5f5", border: "1.5px solid #e0e0e0", borderRadius: 8, color: "#666", fontWeight: 500, padding: "8px 16px", cursor: "pointer", fontSize: 13 },
  tableWrap: { background: "#fff", borderRadius: 10, overflowX: "auto", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", WebkitOverflowScrolling: "touch" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { background: BG, color: "#fff", padding: "11px 14px", textAlign: "left", fontSize: 12, fontWeight: 600 },
  tr: { borderBottom: "1px solid #f5f5f5" },
  td: { padding: "11px 14px", fontSize: 13, color: "#555", verticalAlign: "middle" },
  editBtn: { background: "#f9f9f9", border: "1.5px solid #e0e0e0", borderRadius: 8, color: "#555", padding: "5px 14px", cursor: "pointer", fontSize: 12, display: "inline-flex", alignItems: "center", fontWeight: 500 },
  pagination: { display: "flex", alignItems: "center", gap: 16, marginTop: 16, justifyContent: "flex-end" },
  pgBtn: { background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: 8, color: "#555", padding: "7px 18px", cursor: "pointer", fontSize: 13, fontWeight: 500 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" },
  modal: { background: "#fff", borderRadius: 12, width: 500, maxWidth: "95vw", maxHeight: "90vh", overflow: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.15)" },
  modalHeader: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "20px 24px", borderBottom: "1px solid #f0f0f0" },
  modalTitle: { fontSize: 16, fontWeight: 700, color: BG },
  closeBtn: { background: "none", border: "none", color: "#aaa", fontSize: 18, cursor: "pointer", lineHeight: 1 },
  modalBody: { padding: "20px 24px 28px" },
  fieldRow: { display: "flex", gap: 12 },
  field: { flex: 1 },
  label: { display: "block", color: "#555", fontSize: 13, fontWeight: 600, marginBottom: 6, marginTop: 12 },
  input: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "9px 12px", fontSize: 13, color: "#333", background: "#fafafa", width: "100%", boxSizing: "border-box", outline: "none" },
};
